import { useEffect, useRef, useState, type CSSProperties } from 'react'
import {
  ActionIcon,
  Button,
  Checkbox,
  Menu,
  Paper,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core'
import { modals } from '@mantine/modals'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  IconChevronDown,
  IconDotsVertical,
  IconMessageCircle,
  IconPencil,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react'
import {
  checklistCounts,
  useBoardMutations,
  type Board,
  type BoardColumn,
  type BoardItem,
  type CellProps,
} from '@/pages/Boards/hooks'
import { TextCell } from '@/pages/Boards/cells/TextCell'
import { DateCell } from '@/pages/Boards/cells/DateCell'
import { PriorityCell, StatusCell } from '@/pages/Boards/cells/EnumCell'
import { PersonCell } from '@/pages/Boards/cells/PersonCell'
import { PatientCell } from '@/pages/Boards/cells/PatientCell'
import { AddColumnControl } from '@/pages/Boards/AddColumnControl'
import { openRenameModal } from '@/pages/Boards/renameModal'
import { AiBadge } from '@/pages/Boards/AiBadge'
import { PhoneLink } from '@/components/ui/PhoneLink'
import { isAiGenerated } from './queueFilters'
import {
  dueSeverity,
  MONO,
  QUEUE_COLOR,
  ROW_VERTICAL_SPACING,
  type QueueDensity,
} from './queueTheme'

// Every column keeps a fixed pixel width; the table is sized to their sum with
// table-layout:fixed so columns never shrink as more are added — the wrapper
// scrolls sideways instead.
const COLUMN_PX: Record<string, number> = {
  // Assignee and patient both show avatar + full name, so they get the same room.
  // Patient was previously avatar-only at 100px; names need the width or they
  // truncate to a couple of characters, which is no better than initials.
  person: 210,
  status: 160,
  // 250, not 210: the date slot is a fixed 140px, so the aging badge beside it
  // only gets what is left. The slot grew by 40 when the due date started showing
  // its year (PD-1684), and this grew with it — otherwise the badge keeps the old
  // ~70px and truncates to "9…", which is the number that decides what gets worked
  // next. Sized so a three-digit lateness ("365d late", 77px) still fits.
  date: 250,
  priority: 140,
  patient: 210,
  phone: 150,
  text: 180,
}
const DEFAULT_COLUMN_PX = 180
const ITEM_COLUMN_PX = 430
const STEPS_COLUMN_PX = 120
const ACTIONS_COLUMN_PX = 48
const SELECT_COLUMN_PX = 44

const columnWidth = (type: string): number => COLUMN_PX[type] ?? DEFAULT_COLUMN_PX

// PD-1496: the row actions live in the trailing column, which is the first thing
// pushed off-screen once the table is wider than the viewport — a default board
// already overflows at 1440px, and every custom column makes it worse. Pinning
// the column to the right edge keeps rename/delete (and Add column) reachable
// without horizontal scrolling, whatever the column count. The cell has to paint
// its own background or scrolled rows show through underneath it.
const STICKY_ACTIONS: CSSProperties = {
  position: 'sticky',
  right: 0,
  zIndex: 2,
  borderLeft: `1px solid ${QUEUE_COLOR.hairline}`,
}

// Total messages on an item (top-level notes + replies), shown next to the
// comment bubble so activity is visible from the queue.
const countMessages = (item: BoardItem): number =>
  item.notes.reduce((sum, note) => sum + 1 + note.replies.length, 0)

const Cell = ({ item, column }: CellProps) => {
  switch (column.type) {
    case 'text':
      return <TextCell item={item} column={column} />
    case 'date':
      return <DateCell item={item} column={column} />
    case 'status':
      return <StatusCell item={item} column={column} />
    case 'priority':
      return <PriorityCell item={item} column={column} />
    case 'person':
      return <PersonCell item={item} column={column} />
    case 'patient':
      return <PatientCell item={item} column={column} />
    default:
      return null
  }
}

// PD-1863: the current phone of the row's patient (looked up by the middleware), as a call
// link. The Phone column stores nothing; it reads the board's Patient column wherever it sits.
const PatientPhoneCell = ({
  item,
  patientColumnId,
  patientPhones,
}: {
  item: BoardItem
  patientColumnId: string | undefined
  patientPhones: Record<string, string>
}) => {
  const value = patientColumnId ? item.values[patientColumnId] : undefined
  const phone = typeof value === 'object' && value !== null ? patientPhones[value.reference] : ''
  return phone ? (
    <span className="whitespace-nowrap">
      <PhoneLink phone={phone} />
    </span>
  ) : (
    <span className="text-[11.5px]" style={{ fontFamily: MONO, color: QUEUE_COLOR.subtle }}>
      —
    </span>
  )
}

// Mono, uppercase, letter-spaced: the design's header treatment, which reads as
// a label rather than as another row of content.
const HEADER_CLASS = 'text-left text-[11px] font-normal tracking-[0.11em] uppercase select-none'
const headerStyle = { fontFamily: MONO, color: QUEUE_COLOR.muted }

// A draggable column header. The whole header is the drag handle; PointerSensor's
// distance activation still lets a plain click open the custom-column menu.
const SortableColumnHeader = ({
  column,
  onRename,
  onRemove,
}: {
  column: BoardColumn
  onRename: (column: BoardColumn) => void
  onRemove: (column: BoardColumn) => void
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.id,
  })
  return (
    <Table.Th
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={`cursor-grab touch-none ${HEADER_CLASS}`}
      style={{
        ...headerStyle,
        width: columnWidth(column.type),
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        ...(isDragging ? { position: 'relative', zIndex: 1 } : {}),
      }}
    >
      {column.type === 'text' ? (
        // Custom columns can be renamed or removed via their header menu.
        <Menu shadow="md" position="bottom-start" withinPortal>
          <Menu.Target>
            <button
              type="button"
              className="inline-flex cursor-pointer items-center gap-1 tracking-[0.11em] uppercase transition hover:opacity-70"
            >
              {column.label}
              <IconChevronDown size={12} />
            </button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item leftSection={<IconPencil size={14} />} onClick={() => onRename(column)}>
              Rename column
            </Menu.Item>
            <Menu.Item
              color="red"
              leftSection={<IconTrash size={14} />}
              onClick={() => onRemove(column)}
            >
              Remove column
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      ) : (
        column.label
      )}
    </Table.Th>
  )
}

type QueueTableProps = {
  board: Board
  groupId: string
  items: BoardItem[]
  /** Current phone of each patient on the board, keyed by `Patient/<id>`. */
  patientPhones: Record<string, string>
  onOpenItem: (itemId: string) => void
  allowAdd: boolean
  emptyMessage: string
  selectedIds: Set<string>
  onToggleSelect: (itemId: string) => void
  onToggleSelectAll: (itemIds: string[], select: boolean) => void
  isUnread: (item: BoardItem) => boolean
  density: QueueDensity
  /** Render the Steps column. Decided across both tables so their widths match. */
  showSteps: boolean
  /**
   * Completed work: present, but demoted to grey so the only colour on screen
   * belongs to something that still needs a person.
   */
  quiet?: boolean
}

export const QueueTable = ({
  board,
  groupId,
  items,
  patientPhones,
  onOpenItem,
  allowAdd,
  emptyMessage,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  isUnread,
  density,
  showSteps,
  quiet = false,
}: QueueTableProps) => {
  const [newTask, setNewTask] = useState('')
  const { createItem, renameColumn, reorderColumns, removeColumn, deleteItem, updateItem } =
    useBoardMutations(board.id)

  // The create bar rests at the end of the list, but pins to the viewport bottom
  // whenever that resting place isn't fully on screen, so it's always reachable.
  // This can't be `position: sticky`: StickyScrollArea's wrapper sets overflow-x,
  // which forces overflow-y to compute to `auto`, so that wrapper becomes the
  // sticky containing block even though the window is the vertical scroller —
  // leaving sticky inert. Hence an observer plus position:fixed, as BulkActionBar
  // does. The observer watches the slot, never the bar, because the bar leaves
  // the flow when it pins.
  const barSlotRef = useRef<HTMLDivElement>(null)
  const [barPinned, setBarPinned] = useState(false)

  useEffect(() => {
    const slot = barSlotRef.current
    if (!slot) return
    const observer = new IntersectionObserver(([entry]) => setBarPinned(!entry.isIntersecting), {
      // Fully visible or it pins — a half-cut bar is the case being fixed.
      threshold: 1,
    })
    observer.observe(slot)
    return () => observer.disconnect()
  }, [])

  // Whole-header drag to reorder columns; distance activation keeps clicks (menu)
  // working. Keyboard sensor makes it operable without a mouse.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor),
  )

  const handleColumnDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const from = board.columns.findIndex((c) => c.id === active.id)
    const to = board.columns.findIndex((c) => c.id === over.id)
    if (from === -1 || to === -1) return
    const columnIds = arrayMove(board.columns, from, to).map((c) => c.id)
    void reorderColumns.mutateAsync({ id: board.id, columnIds }).catch(() => undefined)
  }

  const submitNewTask = async () => {
    const name = newTask.trim()
    if (!name || createItem.isPending) return
    try {
      await createItem.mutateAsync({ boardId: board.id, groupId, name })
      setNewTask('')
    } catch {
      // Error notification handled by the mutation hook.
    }
  }

  const confirmDeleteItem = (item: BoardItem) =>
    modals.openConfirmModal({
      title: 'Delete task',
      children: <Text size="sm">Delete “{item.name}”? Its updates will be deleted with it.</Text>,
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => void deleteItem.mutateAsync({ itemId: item.id }).catch(() => undefined),
    })

  const confirmRemoveColumn = (column: BoardColumn) =>
    modals.openConfirmModal({
      title: 'Remove column',
      children: (
        <Text size="sm">Remove the “{column.label}” column from this board for everyone?</Text>
      ),
      labels: { confirm: 'Remove', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () =>
        void removeColumn.mutateAsync({ id: board.id, columnId: column.id }).catch(() => undefined),
    })

  const renameColumnPrompt = (column: BoardColumn) =>
    openRenameModal({
      title: 'Rename column',
      label: 'Column name',
      initial: column.label,
      onSubmit: (label) =>
        void renameColumn
          .mutateAsync({ id: board.id, columnId: column.id, label })
          .catch(() => undefined),
    })

  const renameTaskPrompt = (item: BoardItem) =>
    openRenameModal({
      title: 'Rename task',
      label: 'Task name',
      initial: item.name,
      onSubmit: (name) =>
        void updateItem.mutateAsync({ itemId: item.id, name }).catch(() => undefined),
    })

  const tableWidth =
    SELECT_COLUMN_PX +
    ITEM_COLUMN_PX +
    (showSteps ? STEPS_COLUMN_PX : 0) +
    board.columns.reduce((sum, column) => sum + columnWidth(column.type), 0) +
    ACTIONS_COLUMN_PX

  // Select-all reflects only this table's rows (the queue and Completed tables
  // each have their own header box but share one selection across the page).
  const allSelected = items.length > 0 && items.every((item) => selectedIds.has(item.id))
  const someSelected = items.some((item) => selectedIds.has(item.id))

  // Resolve the built-in columns BY TYPE — ids only happen to equal types on
  // boards the app creates, and the row's urgency bar must not depend on that.
  const dateColumnId = board.columns.find((c) => c.type === 'date')?.id
  const statusColumnId = board.columns.find((c) => c.type === 'status')?.id
  const patientColumnId = board.columns.find((c) => c.type === 'patient')?.id

  return (
    <div>
      {/* DndContext wraps the table (not the <tr>): it renders hidden a11y
          <div>s, which are invalid as a direct child of <tr>. SortableContext
          renders no DOM, so it stays inside the header row. */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleColumnDragEnd}
      >
        <Table
          className="text-sm"
          // The rows draw their own hairlines (a lighter grey than Mantine's, and
          // the design puts them on the TOP edge), so Mantine's are turned off
          // rather than doubled up.
          withRowBorders={false}
          // Row rhythm comes from Mantine's own spacing: its .mantine-Table-td padding
          // shorthand is unlayered CSS, so it beats a Tailwind py-* utility outright.
          verticalSpacing={ROW_VERTICAL_SPACING[density]}
          horizontalSpacing={12}
          style={{ tableLayout: 'fixed', width: tableWidth, borderCollapse: 'collapse' }}
        >
          <Table.Thead>
            <Table.Tr
              style={{
                background: QUEUE_COLOR.surfaceAlt,
                borderTop: `1px solid ${QUEUE_COLOR.hairline}`,
                borderBottom: `1px solid ${QUEUE_COLOR.hairline}`,
              }}
            >
              <Table.Th className="px-3" style={{ width: SELECT_COLUMN_PX }}>
                <Checkbox
                  size="xs"
                  aria-label="Select all tasks"
                  checked={allSelected}
                  indeterminate={someSelected && !allSelected}
                  onChange={(e) =>
                    onToggleSelectAll(
                      items.map((item) => item.id),
                      e.currentTarget.checked,
                    )
                  }
                />
              </Table.Th>
              <Table.Th className={HEADER_CLASS} style={{ ...headerStyle, width: ITEM_COLUMN_PX }}>
                Task
              </Table.Th>
              {showSteps && (
                <Table.Th
                  className={HEADER_CLASS}
                  style={{ ...headerStyle, width: STEPS_COLUMN_PX }}
                >
                  Steps
                </Table.Th>
              )}
              <SortableContext
                items={board.columns.map((c) => c.id)}
                strategy={horizontalListSortingStrategy}
              >
                {board.columns.map((column) => (
                  <SortableColumnHeader
                    key={column.id}
                    column={column}
                    onRename={renameColumnPrompt}
                    onRemove={confirmRemoveColumn}
                  />
                ))}
              </SortableContext>
              <Table.Th
                className="text-center"
                style={{
                  ...STICKY_ACTIONS,
                  width: ACTIONS_COLUMN_PX,
                  background: QUEUE_COLOR.surfaceAlt,
                }}
              >
                <AddColumnControl board={board} />
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((item) => {
              const msgCount = countMessages(item)
              const selected = selectedIds.has(item.id)
              const unread = msgCount > 0 && isUnread(item)
              const { done: doneSteps, total: totalSteps } = checklistCounts(item)
              const done =
                quiet || (statusColumnId ? item.values[statusColumnId] === 'Done' : false)
              const due = dateColumnId ? item.values[dateColumnId] : undefined
              const severity = dueSeverity(typeof due === 'string' ? due : null, done)
              const stepsComplete = totalSteps > 0 && doneSteps === totalSteps
              return (
                <Table.Tr
                  key={item.id}
                  className="group transition-colors hover:bg-[#fafbfd]"
                  style={{
                    background: selected ? '#f5f8ff' : undefined,
                    borderTop: `1px solid ${QUEUE_COLOR.hairline}`,
                  }}
                >
                  <Table.Td>
                    <Checkbox
                      size="xs"
                      aria-label={`Select ${item.name}`}
                      checked={selected}
                      onChange={() => onToggleSelect(item.id)}
                    />
                  </Table.Td>
                  <Table.Td>
                    <div className="flex items-center gap-3">
                      {/* Urgency bar: the row's only always-on colour, so a late
                          task is findable without reading the due column. */}
                      <div
                        className="h-[26px] w-[3px] shrink-0 rounded-[3px]"
                        style={{ background: severity.bar }}
                      />
                      <button
                        type="button"
                        onClick={() => onOpenItem(item.id)}
                        title={item.name}
                        className="min-w-0 flex-1 cursor-pointer truncate text-left text-[15px] tracking-[-0.005em] transition hover:opacity-70"
                        style={{ color: quiet ? QUEUE_COLOR.doneQuiet : QUEUE_COLOR.ink }}
                      >
                        {item.name}
                      </button>
                      {isAiGenerated(item) && <AiBadge size="xs" className="mr-2" />}
                      <Tooltip label={unread ? 'New updates' : 'Open updates'} withArrow>
                        <button
                          type="button"
                          aria-label={`Open updates for ${item.name} (${msgCount}${
                            unread ? ', new' : ''
                          })`}
                          onClick={() => onOpenItem(item.id)}
                          className="mr-2 flex w-9 shrink-0 cursor-pointer items-center gap-0.5 transition hover:opacity-70"
                          style={{ color: unread ? QUEUE_COLOR.wait : QUEUE_COLOR.subtle }}
                        >
                          <IconMessageCircle size={16} fill={unread ? 'currentColor' : 'none'} />
                          {msgCount > 0 && (
                            <span
                              className={`text-[11.5px] ${unread ? 'font-semibold' : ''}`}
                              style={{ fontFamily: MONO }}
                            >
                              {msgCount}
                            </span>
                          )}
                        </button>
                      </Tooltip>
                    </div>
                  </Table.Td>
                  {showSteps && (
                    <Table.Td>
                      {totalSteps > 0 ? (
                        // Opens the task, like the name and the message icon beside it:
                        // the step count is what draws the eye when checking progress,
                        // so it was the thing people tried to click.
                        <button
                          type="button"
                          aria-label={`Open ${item.name} (${doneSteps} of ${totalSteps} ${totalSteps === 1 ? 'step' : 'steps'} done)`}
                          onClick={() => onOpenItem(item.id)}
                          className="flex w-full cursor-pointer flex-col gap-[6px] pr-3 text-left transition hover:opacity-70"
                        >
                          <span
                            className="text-[11.5px] tracking-[0.02em]"
                            style={{
                              fontFamily: MONO,
                              color: doneSteps ? QUEUE_COLOR.ink : QUEUE_COLOR.muted,
                            }}
                          >
                            {doneSteps}/{totalSteps} {totalSteps === 1 ? 'step' : 'steps'}
                          </span>
                          <span
                            className="block h-[3px] w-full overflow-hidden rounded-[3px]"
                            style={{ background: '#edf0f4' }}
                          >
                            <span
                              className="block h-[3px] rounded-[3px]"
                              style={{
                                width: `${Math.round((doneSteps / totalSteps) * 100)}%`,
                                background: stepsComplete ? QUEUE_COLOR.done : QUEUE_COLOR.wait,
                              }}
                            />
                          </span>
                        </button>
                      ) : (
                        <span
                          className="pr-3 text-[11.5px]"
                          style={{ fontFamily: MONO, color: QUEUE_COLOR.subtle }}
                        >
                          —
                        </span>
                      )}
                    </Table.Td>
                  )}
                  {board.columns.map((column) => (
                    <Table.Td key={column.id}>
                      {column.type === 'phone' ? (
                        <PatientPhoneCell
                          item={item}
                          patientColumnId={patientColumnId}
                          patientPhones={patientPhones}
                        />
                      ) : (
                        <Cell item={item} column={column} />
                      )}
                    </Table.Td>
                  ))}
                  <Table.Td
                    className={`text-center ${
                      selected
                        ? 'bg-[#f5f8ff]'
                        : quiet
                          ? 'bg-[#fafbfc] group-hover:bg-[#fafbfd]'
                          : 'bg-white group-hover:bg-[#fafbfd]'
                    }`}
                    style={STICKY_ACTIONS}
                  >
                    <Menu shadow="md" position="bottom-end" withinPortal>
                      <Menu.Target>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size="sm"
                          aria-label={`Actions for ${item.name}`}
                        >
                          <IconDotsVertical size={14} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          leftSection={<IconPencil size={14} />}
                          onClick={() => renameTaskPrompt(item)}
                        >
                          Rename task
                        </Menu.Item>
                        <Menu.Item
                          color="red"
                          leftSection={<IconTrash size={14} />}
                          onClick={() => confirmDeleteItem(item)}
                        >
                          Delete task
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                </Table.Tr>
              )
            })}

            {items.length === 0 && (
              <Table.Tr style={{ borderTop: `1px solid ${QUEUE_COLOR.hairline}` }}>
                <Table.Td colSpan={board.columns.length + (showSteps ? 4 : 3)}>
                  <p className="py-8 text-center text-sm" style={{ color: QUEUE_COLOR.subtle }}>
                    {emptyMessage}
                  </p>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </DndContext>

      {allowAdd && (
        // The slot holds the bar's place in the list. It keeps its height while the
        // bar is pinned — drop that and the page shortens the moment the bar leaves
        // the flow, which scrolls the slot back into view and oscillates.
        <div
          ref={barSlotRef}
          className="h-12"
          style={{ borderTop: `1px solid ${QUEUE_COLOR.hairline}` }}
        >
          <Paper
            shadow={barPinned ? 'md' : undefined}
            withBorder={barPinned}
            radius="md"
            // bottom-6 clears StickyScrollArea's own scrollbar (fixed bottom-0, h-4).
            className={barPinned ? 'fixed bottom-6 left-6 z-40' : undefined}
          >
            <div className="flex items-center gap-2 p-1.5">
              <TextInput
                value={newTask}
                onChange={(e) => setNewTask(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void submitNewTask()
                  if (e.key === 'Escape') setNewTask('')
                }}
                disabled={createItem.isPending}
                size="sm"
                placeholder="Enter a new task…"
                aria-label="New task name"
                className="w-[240px]"
              />
              <Button
                size="sm"
                leftSection={<IconPlus size={14} />}
                disabled={!newTask.trim()}
                loading={createItem.isPending}
                onClick={() => void submitNewTask()}
              >
                Add task
              </Button>
            </div>
          </Paper>
        </div>
      )}
    </div>
  )
}
